<template>
  <SogCard>
    <SogCardTitle>Berechtigungen</SogCardTitle>
    <SogCardContent>
      <SogGrid
        ref="_grid"
        :data="_data"
        :columns="_columns"
        allow-paging="false"
        hide-scroll="true"
        :on-row-selected="onRowSelected"
        :allow-selection="_edit"
      />
    </SogCardContent>
    <SogCardActions>
      <SogCardActionButton
        v-show="!_edit"
        mdi-icon="mdi-pencil"
        @click="onEdit"
      />
      <SogCardActionButton
        v-show="_edit"
        mdi-icon="mdi-content-save"
        @click="onSave"
      />
      <SogCardActionButton
        v-show="_edit"
        mdi-icon="mdi-cancel"
        @click="onCancel"
      />
    </SogCardActions>
  </SogCard>
</template>

<script setup lang="ts">
import {onBeforeRouteUpdate} from "vue-router";
import {onMounted, reactive, ref, watch} from "vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {BooleanColumn, DefaultColumn} from "@/shared/ui/types/SogGridColumnDefinitions";
import SogGrid from "@/shared/ui/SogGrid.vue";
import {useAccountStore} from "@/stores/accountStore";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {Action, lossAdjusterActions, superActions, usNatoActions} from "@/shared/model/Action";
import notificationService from "@/shared/services/NotificationService";
import apiOrganisationStructure from "@/modules/OrganisationStructure/api";
import PersonIds from "@/shared/model/PersonIds";
import {useSpinner} from "@/shared/composables/SogSpinner";

interface Props {
  personId?: string
  actions?: string[]
}

const props = withDefaults(defineProps<Props>(), {
    personId: undefined, actions: undefined

  }
)

const _columns: ColumnModel[] = [
  /*
  { type: 'checkbox' },
*/
  {...DefaultColumn, field: 'id', headerText: 'id', isPrimaryKey: true, visible: false},
  {...BooleanColumn, field: 'checked', headerText: ''},
  {...DefaultColumn, field: 'description', headerText: 'Berechtigung'},
]


const _data = reactive<{ id: string, checked: boolean, description: string }[]>([])
const _accountStore = useAccountStore();
const _grid = ref<InstanceType<typeof SogGrid> | null>(null)
const _edit = ref(false)
const _spinner = useSpinner()


watch(props, () => {
  init();

  /*
    const array = _accountStore.getManagedActions().map(t=>{
      const checked = props.actions ? props.actions.some(a=>a.toLowerCase()===t.id.toLowerCase()):false;
      const a = {...t,checked : checked}
      return a;
    })
    _data.push(...array)
    _grid.value?.refresh();
  */

}, {immediate: true})

function init() {
  _data.length = 0;

  let array = _accountStore.getManagedActions();

  if (_accountStore.hasAction(Action.SuperAccountManage) || _accountStore.actionPerson?.id === "person_getmann") {
    array.push(...superActions)
  }


  if (props.personId === PersonIds.UsNato || _accountStore.person?.id === PersonIds.UsNato)
    array = [...usNatoActions];

  if (_accountStore.person?.isLossAdjuster())
    array = [...lossAdjusterActions];

  array = array.map(t => {
    const checked = props.actions ? props.actions.some(a => a.toLowerCase() === t.id.toLowerCase()) : false;
    return {...t, checked: checked};
  })
  _data.push(...array)
  _grid.value?.refresh();
}

onMounted(async () => {
  await init();
})


onBeforeRouteUpdate(async () => {
  await init();
})

function onEdit() {
  if (!props.personId) {
    notificationService.error("Person ist unbekannt")
    return;
  }
  if (!_accountStore.hasAction(Action.AccountManage)) {
    notificationService.error("Sie haben nicht die notwendige Brechtigungen")
    return;
  }
  _edit.value = true
}

function onCancel() {
  _edit.value = false
  init();
}

async function onSave() {
  try {
    _spinner.show()
    const actions = _data.filter(t => t.checked).map(t => t.id)
    const res = await apiOrganisationStructure.accountChangeActions(props.personId ?? "", actions)
    if (res) {
      _spinner.hide()
      notificationService.success("Berechtigungen wurde zugeteilt")
      _edit.value = false
    }
  } finally {
    _spinner.hide()
  }
}

function onRowSelected(args: any) {
  const i = _data.findIndex((t) => {
    return t.id === args.data.id
  })
  _data[i].checked = !_data[i].checked;
  _grid.value.refresh();
}

</script>

<style>

</style>
